import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowCounterClockwise, ArrowsClockwise, CheckCircle, Lightbulb, PencilSimple, Sparkle } from "@phosphor-icons/react";

/**
 * Step 2 of CreateSong: Lyrics review/edit dual-mode flow.
 *
 * Review mode (default): read-only lyrics + "Autorizar letra" / "Modificar letra".
 * Edit mode: editable textarea + regen panel + "Ya quedó lista la canción".
 *
 * Audio generation only kicks off after `onApprove` is called.
 */
export const LyricsStep = ({
  lyrics,
  setLyrics,
  originalLyrics,
  finalTitle,
  setFinalTitle,
  editMode,
  setEditMode,
  onApprove,
  onBack,
  onOpenRegen,
  regenInfo,
}) => {
  const nav = useNavigate();

  const origLines = originalLyrics.split("\n");
  const curLines = lyrics.split("\n");
  let editedLineCount = 0;
  const maxLen = Math.max(origLines.length, curLines.length);
  for (let i = 0; i < maxLen; i++) {
    if ((origLines[i] || "") !== (curLines[i] || "")) editedLineCount += 1;
  }
  const hasEdits = editedLineCount > 0 && originalLyrics.length > 0;

  return (
    <div data-testid="step-2">
      <p className="text-sm uppercase tracking-widest text-[#FFB800] mb-2">Paso 2 de 3</p>
      <h1 className="font-display text-3xl sm:text-4xl font-bold mb-2">Tu letra</h1>
      <p className="text-white/60 mb-4">
        {editMode
          ? <>Edítala libremente. Cuando estés conforme, marca <span className="text-[#B4F34A] font-medium">«Ya quedó lista»</span>.</>
          : <>Lee tu letra. Si te gustó, <span className="text-[#B4F34A] font-medium">autorízala</span>; si quieres ajustar algo, púlsale a <span className="text-[#FFB800] font-medium">«Modificar»</span>.</>}
      </p>

      <div className="mb-4">
        <input
          value={finalTitle}
          onChange={(e) => setFinalTitle(e.target.value)}
          readOnly={!editMode}
          className={`w-full px-4 py-3 rounded-xl bg-[#121212] border border-white/10 font-display text-xl font-bold ${editMode ? "focus:border-[#FFB800] focus:outline-none" : "opacity-90"}`}
          data-testid="lyrics-title"
        />
      </div>

      {!editMode ? (
        <>
          <div className="relative bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 mb-4" data-testid="lyrics-review-block">
            <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#FFB800]/10 border border-[#FFB800]/30 pointer-events-none">
              <Sparkle size={11} weight="fill" className="text-[#FFB800]" />
              <span className="text-[10px] uppercase tracking-widest text-[#FFB800]/90">IA</span>
            </div>
            <pre className="whitespace-pre-wrap font-sans text-white/90 leading-loose text-[15px] pt-6" data-testid="lyrics-review-pre">{lyrics}</pre>
          </div>

          <div className="rounded-2xl bg-[#B4F34A]/5 border border-[#B4F34A]/20 p-4 mb-5 flex items-start gap-3">
            <Lightbulb size={20} weight="fill" className="text-[#B4F34A] shrink-0 mt-0.5" />
            <p className="text-xs text-white/70 leading-relaxed">
              <span className="text-white/90 font-semibold">Solo se generará el audio cuando autorices la letra.</span> Si quieres ajustar algo —cambiar una palabra, un verso, o regenerarla entera— elige «Modificar letra».
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <button
              onClick={onApprove}
              className="flex-1 py-4 rounded-full bg-[#B4F34A] hover:bg-[#A0E03A] text-black font-bold transition-all active:scale-95 flex items-center justify-center gap-2"
              data-testid="btn-authorize-lyrics"
            >
              <CheckCircle weight="fill" size={20} /> Autorizar letra
            </button>
            <button
              onClick={() => setEditMode(true)}
              className="flex-1 py-4 rounded-full bg-transparent border-2 border-[#FFB800] text-[#FFB800] hover:bg-[#FFB800]/10 font-bold transition-all active:scale-95 flex items-center justify-center gap-2"
              data-testid="btn-modify-lyrics"
            >
              <PencilSimple weight="bold" size={20} /> Modificar letra
            </button>
          </div>

          <button
            onClick={onBack}
            className="text-sm text-white/50 hover:text-white/80 inline-flex items-center gap-1.5 mx-auto"
            data-testid="btn-back-from-lyrics"
          >
            <ArrowLeft size={14} /> Volver al paso anterior
          </button>
        </>
      ) : (
        <>
          <div className="rounded-2xl bg-[#B4F34A]/5 border border-[#B4F34A]/20 p-4 mb-4 flex items-start gap-3" data-testid="manual-edit-hint">
            <Lightbulb size={20} weight="fill" className="text-[#B4F34A] shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white/90 font-medium">Tip: editar la letra a mano es <span className="text-[#B4F34A] font-bold">gratis</span> y al instante.</p>
              <p className="text-xs text-white/50 mt-1">Si sólo quieres cambiar una palabra o un par de líneas, hazlo aquí abajo. Sólo regenera si quieres una letra completamente nueva.</p>
            </div>
            {hasEdits && (
              <div className="hidden sm:flex flex-col items-end shrink-0" data-testid="manual-edit-counter">
                <span className="text-xs uppercase tracking-widest text-[#B4F34A] font-bold">{editedLineCount} línea{editedLineCount === 1 ? "" : "s"}</span>
                <span className="text-[10px] text-white/40">editada{editedLineCount === 1 ? "" : "s"} a mano</span>
              </div>
            )}
          </div>

          <div className={`relative bg-[#0a0a0a] border rounded-2xl p-5 mb-3 transition-colors ${hasEdits ? "border-[#B4F34A]/40" : "border-white/10 hover:border-white/20 focus-within:border-[#FFB800]/40"}`}>
            <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/5 border border-white/10 pointer-events-none">
              <PencilSimple size={11} className="text-[#B4F34A]" />
              <span className="text-[10px] uppercase tracking-widest text-white/60">Editable</span>
            </div>
            <textarea
              rows={18}
              value={lyrics}
              onChange={(e) => setLyrics(e.target.value)}
              placeholder="Tu letra aparecerá aquí…"
              spellCheck={true}
              autoFocus
              className="w-full bg-transparent text-white/90 leading-loose font-mono text-sm focus:outline-none resize-none cursor-text caret-[#FFB800] selection:bg-[#FFB800]/30 pt-6"
              data-testid="lyrics-textarea"
            />
          </div>

          {hasEdits && (
            <div className="flex items-center justify-between gap-3 mb-6 px-1" data-testid="restore-ai-row">
              <p className="text-xs text-white/50 flex-1">
                Mostrando tu versión editada · {editedLineCount} línea{editedLineCount === 1 ? "" : "s"} diferente{editedLineCount === 1 ? "" : "s"} a la original.
              </p>
              <button
                type="button"
                onClick={() => {
                  if (!window.confirm("¿Restaurar la letra original de la IA? Perderás tus ediciones manuales.")) return;
                  setLyrics(originalLyrics);
                  toast.success("Letra original restaurada");
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 transition-colors"
                data-testid="restore-ai-btn"
              >
                <ArrowCounterClockwise size={12} /> Restaurar versión IA
              </button>
            </div>
          )}
          {!hasEdits && <div className="mb-6" />}

          {regenInfo && (() => {
            const lyricsInfo = regenInfo.kinds?.lyrics || {};
            const isFreeNext = !!lyricsInfo.is_free_next;
            const credits = regenInfo.regen_credits || 0;
            const canRegen = isFreeNext || credits > 0;
            const hint = isFreeNext
              ? "Tienes 1 cambio gratis incluido"
              : credits > 0
                ? `Cuesta 1 cambio extra ($20 MXN). Tienes ${credits} disponible${credits === 1 ? "" : "s"}.`
                : "Sin cambios extra. Compra cambios para regenerar.";
            return (
              <div className="rounded-2xl bg-[#0a0a0a] border border-white/10 p-4 mb-6 flex items-start gap-3 flex-wrap" data-testid="regen-lyrics-panel">
                <div className="flex items-center gap-2 shrink-0">
                  <ArrowsClockwise size={18} weight="bold" className="text-[#B4F34A]" />
                  <span className="text-sm font-semibold text-white">¿No te gustó la letra?</span>
                </div>
                <p className="text-xs text-white/50 flex-1 min-w-[200px]">
                  {hint} <span className="text-white/70">· Tip: editarla a mano arriba es gratis.</span>
                </p>
                <button
                  type="button"
                  onClick={() => canRegen ? onOpenRegen() : nav("/cambios-extra")}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all active:scale-95 ${
                    canRegen
                      ? "bg-[#B4F34A] hover:bg-[#A0E03A] text-black"
                      : "bg-white/5 border border-white/10 text-white/80 hover:bg-white/10"
                  }`}
                  data-testid="btn-open-regen-lyrics"
                >
                  {canRegen
                    ? (isFreeNext ? "Regenerar (gratis)" : "Regenerar (-1 cambio)")
                    : "Comprar cambios extra"}
                </button>
              </div>
            );
          })()}

          <div className="flex gap-3">
            <button
              onClick={() => setEditMode(false)}
              className="px-5 py-3 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 font-medium transition-all flex items-center gap-2"
              data-testid="btn-cancel-edit-lyrics"
            >
              <ArrowLeft size={18} /> Volver a revisión
            </button>
            <button
              onClick={onApprove}
              className="flex-1 py-3 rounded-full bg-[#B4F34A] hover:bg-[#A0E03A] text-black font-bold transition-all active:scale-95 flex items-center justify-center gap-2"
              data-testid="btn-approve-lyrics"
            >
              Ya quedó lista la canción <CheckCircle weight="fill" size={18} />
            </button>
          </div>
        </>
      )}
    </div>
  );
};
